import { IconMinus, IconPlus } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, { useEffect, useState } from 'react';
import NSLabel from 'bricks/NSLabel/NSLabel';
import {
    Input, InputGroup, InputGroupAddon, InputGroupText, UncontrolledTooltip,
} from 'reactstrap';
import RenderIf from 'components/RenderIf/RenderIf';
import 'css/partials/_bricks.NSInputNumberStepper.scss';
import useDebounce from 'hooks/useDebounce';

export interface INSInputNumberStepperProps {
    name: string;
    id?: string;
    labelClassName?: string;
    labelText?: string;
    value: number | null;
    inputGroupClassName?: string;
    min?: number;
    max?: number;
    onChange?: any;
    icon?: any;
    iconTooltipText?: string;
    required?: boolean;
    step: number;
    percentage?: boolean;
    customPrefix?: string;
    customSuffix?: string;
    showErrorMsg?: boolean;
    errorMsg?: string;
    onBlur?: (valueToUpdate: string) => void;
    disabled?: boolean;
}

const NSInputNumberStepper = ({
    name,
    id,
    labelClassName = '',
    labelText = '',
    value,
    inputGroupClassName,
    min,
    max,
    onChange,
    step,
    icon,
    iconTooltipText,
    customPrefix,
    customSuffix,
    required = false,
    percentage = false,
    showErrorMsg,
    errorMsg,
    onBlur,
    disabled = false,
}: INSInputNumberStepperProps) => {
    const [intermediateValue, setIntermediateValue] = useState<string>('');
    const [previousValue, setPreviousValue] = useState<string>('');
    const [shouldTriggerBlur, setShouldTriggerBlur] = useState(false);
    const [forceBlur, setForceBlur] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const debouncedIntermediateValue = useDebounce(intermediateValue, 500);

    useEffect(() => {
        if (debouncedIntermediateValue && shouldTriggerBlur) {
            handleBlur(intermediateValue);
            setShouldTriggerBlur(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedIntermediateValue]);

    useEffect(() => {
        if (forceBlur) {
            setTimeout(() => {
                setForceBlur(false);
            }, 1000);
        }
    }, [forceBlur]);

    const getDisplayValue = (val: string) => {
        if (Number.isNaN(Number(val))) return '';
        if (percentage && val.length) {
            // For display only, show 2 decimals
            const num = Number(val);
            return `${num.toFixed(2)}%`;
        }
        if (val.length) {
            let displayValue = val;
            if (customPrefix) displayValue = `${customPrefix}${displayValue}`;
            if (customSuffix) displayValue = `${displayValue} ${customSuffix}`;
            return displayValue;
        }
        return val;
    };

    const getInitialValue = () => {
        if (value === null) return '';

        if (percentage) {
            const percentageValue = Number(Number(value) * 100).toFixed(10); // to ensure precision
            return String(Number(percentageValue)); // remove trailing zeros before setting to string
        }

        return `${value}`;
    };

    useEffect(() => {
        const initialValue = getInitialValue();
        setIntermediateValue(initialValue);
        setPreviousValue(initialValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, percentage]);

    const countDown = (prevValue: string) => {
        let newValue = Number(prevValue);
        const stepDecimals = step.toString().split('.')[1]?.length || 0;
        const valueDecimals = prevValue.split('.')[1]?.length || 0;

        if (stepDecimals === 0 && valueDecimals > 0) {
            // If step is whole number but value has decimals, first round down
            newValue = Math.floor(newValue);
        } else {
            newValue -= step;
        }
        if (min !== undefined) newValue = Math.max(min, newValue);
        newValue = Math.max(0, newValue);

        setIntermediateValue(Number(newValue.toFixed(10)).toString());
        setShouldTriggerBlur(true);
    };

    const countUp = (prevValue: string) => {
        let newValue = Number(prevValue);
        const stepDecimals = step.toString().split('.')[1]?.length || 0;
        const valueDecimals = prevValue.split('.')[1]?.length || 0;

        if (stepDecimals === 0 && valueDecimals > 0) {
            // If step is whole number but value has decimals, first round up
            newValue = Math.ceil(newValue);
        } else {
            newValue += step;
        }
        if (max !== undefined) newValue = Math.min(max, newValue);

        setIntermediateValue(Number(newValue.toFixed(10)).toString());
        setShouldTriggerBlur(true);
    };

    const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;

        if (newValue === '.') {
            setIntermediateValue('0.');
            return;
        }

        if (newValue === '') {
            setIntermediateValue('');
            return;
        }

        const cleanValue = newValue.replace(/[^0-9.]/g, '') // Keep only numbers and '.'
            .replace(/^(\d*\.\d*)\.+/, '$1') // Prevent multiple decimals
            .replace(/^\./, '0.'); // Add leading zero for decimals

        setIntermediateValue(cleanValue);
    };

    const handleChange = (value: string) => {
        if (value === '') {
            onChange(null);
            return;
        }

        let numericValue = parseFloat(value);

        if (Number.isNaN(numericValue)) return;

        if (value.endsWith('.')) {
            numericValue = Number(value.slice(0, -1));
        }

        if (min !== undefined) numericValue = Math.max(min, numericValue);
        if (max !== undefined) numericValue = Math.min(max, numericValue);

        numericValue = Number(numericValue.toFixed(10));

        if (percentage) {
            onChange(numericValue / 100);
        } else {
            onChange(numericValue);
        }
    };

    const handleBlur = (valueToUpdate: string) => {
        if (valueToUpdate !== previousValue) {
            if (onBlur) {
                onBlur(valueToUpdate);
            } else {
                handleChange(intermediateValue);
            }
            setPreviousValue(valueToUpdate);
        }
        setIsFocused(false);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowUp') {
            countUp(intermediateValue);
        } else if (event.key === 'ArrowDown') {
            countDown(intermediateValue);
        } else if (event.key === 'Enter') {
            if (intermediateValue !== previousValue) {
                handleBlur(intermediateValue);
            }
            setForceBlur(true);
        } else if (event.key === 'Escape') {
            setIntermediateValue(previousValue);
            setForceBlur(true);
            setIsFocused(false);
        }
    };

    return (
        <>
            <RenderIf isTrue={labelText}>
                <NSLabel for={name} className={labelClassName}>
                    {labelText}
                    <RenderIf isTrue={required}>
                        <span className="text-danger ml-1">*</span>
                    </RenderIf>
                    <RenderIf isTrue={icon}>
                        <NSIcon icon={icon} className="pl-1 cursor--pointer" id="numberStepperIcon" />
                        <RenderIf isTrue={iconTooltipText}>
                            <UncontrolledTooltip target="numberStepperIcon">{iconTooltipText}</UncontrolledTooltip>
                        </RenderIf>
                    </RenderIf>
                </NSLabel>
            </RenderIf>
            <InputGroup className={`d-flex NSInputNumberStepper ${disabled ? 'NSInputNumberStepper--disabled' : ''} ${inputGroupClassName}`}>
                <InputGroupAddon addonType="prepend" className="NSInputNumberStepper__addon-prepend cursor--pointer">
                    <InputGroupText className="py-0 NSInputNumberStepper__addon-prepend__text" onClick={() => countDown(intermediateValue)}>
                        <NSIcon icon={IconMinus} data-testid="prependButton" />
                    </InputGroupText>
                </InputGroupAddon>
                <Input
                    type="text"
                    id={id}
                    name={name}
                    aria-label={name}
                    value={isFocused ? intermediateValue : getDisplayValue(intermediateValue)}
                    placeholder="Enter"
                    className="NSInputNumberStepper__input border-right-0 border-left-0 text-center"
                    onChange={handleNumberChange}
                    onBlur={() => handleBlur(intermediateValue)}
                    onKeyDown={handleKeyDown}
                    min={min}
                    max={max}
                    disabled={forceBlur || disabled}
                    onFocus={() => setIsFocused(true)}
                />
                <InputGroupAddon addonType="append" className="NSInputNumberStepper__addon-append cursor--pointer">
                    <InputGroupText className="py-0 NSInputNumberStepper__addon-append__text" onClick={() => countUp(intermediateValue)}>
                        <NSIcon icon={IconPlus} data-testid="appendButton" />
                    </InputGroupText>
                </InputGroupAddon>
            </InputGroup>

            <RenderIf isTrue={showErrorMsg}>
                <p className="text-danger small mt-1">{errorMsg}</p>
            </RenderIf>
        </>
    );
};

export default NSInputNumberStepper;
